// Attribution — two modes:
//  • ABSOLUTE — contribution to the portfolio's own TWR (strategy = Cariño-exact,
//    security = approx P&L-delta). Answers "what made my return".
//  • ACTIVE vs SPY — Brinson-Fachler: where the ALPHA came from (sector allocation +
//    selection, per-security active contribution). Answers "what made my alpha".
//    Headlines the REALIZED alpha (windowed TWR − benchmark); the decomposition uses
//    point-in-time look-through weights so it's directional (signs/ranking robust,
//    absolute bps approximate) — the residual vs realized is shown, never hidden.
// See Plans/performance_and_risk_tabs.md §2.4.
import React, { useEffect, useMemo, useState } from 'react';
import { tradingService } from '../../../services/tradingService';
import { useSortableRows, SortableTh } from '../../../components/tables/SortableTable';

const pct = (v, dp = 2) => (v == null || isNaN(v) ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(dp)}%`);
const clr = (v) => (v > 0 ? 'text-emerald-500' : v < 0 ? 'text-red-500' : 'text-gray-400');
const TOP = 10;

// Horizontal diverging bar (green right / red left), centered at 0.
const Bar = ({ v, maxAbs }) => {
  const w = Math.min(50, (Math.abs(v) / (maxAbs || 1)) * 50);
  return (
    <div className="relative h-3 flex-1 min-w-[70px]">
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 dark:bg-gray-600" />
      {v >= 0
        ? <div className="absolute top-0.5 h-2 rounded-r bg-emerald-500/70" style={{ left: '50%', width: `${w}%` }} />
        : <div className="absolute top-0.5 h-2 rounded-l bg-red-500/70" style={{ right: '50%', width: `${w}%` }} />}
    </div>
  );
};

// ── ABSOLUTE view ────────────────────────────────────────────────────────────
const ContribList = ({ title, rows, labelKey, note, expandable }) => {
  const [expanded, setExpanded] = useState(false);
  const maxAbs = useMemo(() => Math.max(1e-6, ...rows.map((r) => Math.abs(r.contribution))), [rows]);
  const shown = expanded ? rows : rows.slice(0, TOP);
  return (
    <div>
      <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-2">{title}{note && <span className="ml-1 text-gray-400 font-normal">{note}</span>}</div>
      <div className="space-y-1">
        {shown.map((r) => (
          <div key={r[labelKey]} className="grid grid-cols-[120px_1fr_58px] items-center gap-2">
            <div className="text-[11px] text-gray-600 dark:text-gray-300 truncate" title={r[labelKey]}>{r[labelKey]}</div>
            <Bar v={r.contribution} maxAbs={maxAbs} />
            <div className={`text-right text-[11px] tabular-nums ${clr(r.contribution)}`}>{pct(r.contribution)}</div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-xs text-gray-400 italic">No contributions in this window.</div>}
      </div>
      {expandable && rows.length > TOP && (
        <button onClick={() => setExpanded((x) => !x)} className="mt-2 text-[11px] text-brand-gold hover:underline">
          {expanded ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
};

const AbsoluteView = ({ data }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
    <ContribList title="By strategy" note="(exact)" rows={data.strategy || []} labelKey="strategy" expandable />
    <ContribList title="By security" note={data.security_approx ? '(approx P&L-delta)' : ''} rows={data.security || []} labelKey="symbol" expandable />
  </div>
);

// ── ACTIVE (vs benchmark) view ───────────────────────────────────────────────
const Stat = ({ label, value, tone }) => (
  <div>
    <div className="text-[9px] uppercase tracking-wider text-gray-400">{label}</div>
    <div className={`text-lg font-serif tabular-nums ${tone}`}>{value}</div>
  </div>
);

// Daily method computes sector TOTALS only (no allocation/selection split) → compact table.
const SectorTotals = ({ rows }) => {
  const [expanded, setExpanded] = useState(false);
  const maxAbs = useMemo(() => Math.max(1e-6, ...rows.map((r) => Math.abs(r.total))), [rows]);
  const shown = expanded ? rows : rows.slice(0, 8);
  return (
    <div>
      <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-2">By sector <span className="font-normal text-gray-400">— active contribution</span></div>
      <div className="space-y-1">
        {shown.map((r) => (
          <div key={r.sector} className="grid grid-cols-[120px_1fr_58px] items-center gap-2">
            <div className="text-[11px] text-gray-600 dark:text-gray-300 truncate" title={r.sector}>{r.sector}</div>
            <Bar v={r.total} maxAbs={maxAbs} />
            <div className={`text-right text-[11px] tabular-nums ${clr(r.total)}`}>{pct(r.total)}</div>
          </div>
        ))}
      </div>
      {rows.length > 8 && (
        <button onClick={() => setExpanded((x) => !x)} className="mt-2 text-[11px] text-brand-gold hover:underline">
          {expanded ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
};

const SECTOR_BRINSON_COLS = [
  { key: 'sector', type: 'string', get: (r) => r.sector },
  { key: 'active_weight', type: 'number', get: (r) => r.active_weight },
  { key: 'allocation', type: 'number', get: (r) => r.allocation },
  { key: 'selection', type: 'number', get: (r) => r.selection },
  { key: 'total', type: 'number', get: (r) => r.total },
];

const SectorBrinson = ({ rows, totals }) => {
  const [expanded, setExpanded] = useState(false);
  const { sorted, toggle, indicator } = useSortableRows(rows, SECTOR_BRINSON_COLS);
  const shown = expanded ? sorted : sorted.slice(0, 8);
  return (
    <div>
      <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-2">
        By sector <span className="font-normal text-gray-400">— allocation (tilt) + selection (picks)</span>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-[11px] min-w-[340px]">
          <thead>
            <tr className="text-[9px] uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-800">
              <SortableTh label="Sector" colKey="sector" align="left" className="py-1 pr-2" toggle={toggle} indicator={indicator} />
              <SortableTh label="Active" colKey="active_weight" className="py-1 px-1.5" toggle={toggle} indicator={indicator} />
              <SortableTh label="Alloc" colKey="allocation" className="py-1 px-1.5" toggle={toggle} indicator={indicator} />
              <SortableTh label="Select" colKey="selection" className="py-1 px-1.5" toggle={toggle} indicator={indicator} />
              <SortableTh label="Total" colKey="total" className="py-1 pl-1.5" toggle={toggle} indicator={indicator} />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60">
            {shown.map((r) => (
              <tr key={r.sector} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                <td className="py-1 pr-2 text-gray-600 dark:text-gray-300 truncate max-w-[120px]" title={r.sector}>{r.sector}</td>
                <td className={`py-1 px-1.5 text-right tabular-nums ${clr(r.active_weight)}`}>{pct(r.active_weight, 1)}</td>
                <td className={`py-1 px-1.5 text-right tabular-nums ${clr(r.allocation)}`}>{pct(r.allocation)}</td>
                <td className={`py-1 px-1.5 text-right tabular-nums ${clr(r.selection)}`}>{pct(r.selection)}</td>
                <td className={`py-1 pl-1.5 text-right tabular-nums font-medium ${clr(r.total)}`}>{pct(r.total)}</td>
              </tr>
            ))}
          </tbody>
          {totals && (
            <tfoot>
              <tr className="border-t border-gray-200 dark:border-gray-700 text-[11px] font-medium">
                <td className="py-1 pr-2 text-gray-500 uppercase tracking-wide text-[9px]">Total</td>
                <td className="py-1 px-1.5"></td>
                <td className={`py-1 px-1.5 text-right tabular-nums ${clr(totals.allocation)}`}>{pct(totals.allocation)}</td>
                <td className={`py-1 px-1.5 text-right tabular-nums ${clr(totals.selection)}`}>{pct(totals.selection)}</td>
                <td className={`py-1 pl-1.5 text-right tabular-nums ${clr(totals.total)}`}>{pct(totals.total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {rows.length > 8 && (
        <button onClick={() => setExpanded((x) => !x)} className="mt-2 text-[11px] text-brand-gold hover:underline">
          {expanded ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
};

const SecurityActive = ({ rows, benchmark }) => {
  const [expanded, setExpanded] = useState(false);
  // Rank best → worst (contributors on top, detractors at the bottom).
  const sorted = useMemo(() => [...rows].sort((a, b) => b.active_contribution - a.active_contribution), [rows]);
  const maxAbs = useMemo(() => Math.max(1e-6, ...sorted.map((r) => Math.abs(r.active_contribution))), [sorted]);
  const shown = expanded ? sorted : [...sorted.slice(0, 8), ...sorted.slice(-6)];
  const seen = new Set();
  const dedup = shown.filter((r) => (seen.has(r.symbol) ? false : seen.add(r.symbol)));
  return (
    <div>
      <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-2">
        By security <span className="font-normal text-gray-400">— active wt × (return − {benchmark})</span>
      </div>
      <div className="space-y-1">
        {dedup.map((r) => (
          <div key={r.symbol} className="grid grid-cols-[132px_1fr_58px] items-center gap-2">
            <div className="text-[11px] text-gray-600 dark:text-gray-300 truncate flex items-baseline gap-1" title={r.symbol}>
              <span>{r.symbol}</span>
              <span className={`text-[9px] tabular-nums ${clr(r.active_weight)}`}>{pct(r.active_weight, 1)}</span>
              <span className="text-[9px] text-gray-400 tabular-nums">r {pct(r.ret, 0)}</span>
            </div>
            <Bar v={r.active_contribution} maxAbs={maxAbs} />
            <div className={`text-right text-[11px] tabular-nums ${clr(r.active_contribution)}`}>{pct(r.active_contribution)}</div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-xs text-gray-400 italic">No active exposure in this window.</div>}
      </div>
      {rows.length > 14 && (
        <button onClick={() => setExpanded((x) => !x)} className="mt-2 text-[11px] text-brand-gold hover:underline">
          {expanded ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
};

const ActiveView = ({ data, realizedPort, realizedBench, scopeLabel }) => {
  const bm = data.benchmark || 'SPY';
  // Prefer the caller's rebased windowReturn (the SAME numbers as the Return tile + chart)
  // over the backend's recompute, so this headline can never disagree with them.
  const port = realizedPort != null ? realizedPort : data.realized_port_return;
  const bench = realizedBench != null ? realizedBench : data.realized_benchmark_return;
  const hasRealized = port != null && bench != null;
  const realizedActive = hasRealized ? port - bench : null;
  const headline = hasRealized ? realizedActive : data.active_return;
  const residual = hasRealized ? (data.active_return - realizedActive) : data.residual;
  const isDaily = data.method === 'daily';
  const anchorNote = data.weight_fallback
    ? `${data.weight_as_of || 'latest'}${data.weight_as_of ? ' (first date positions were held)' : ' (no holdings in window)'}`
    : (data.benchmark_as_of || data.weight_as_of || 'latest');
  return (
    <div className="space-y-4">
      {/* Realized alpha headline (ground truth) */}
      <div className="flex items-end justify-between gap-3 flex-wrap bg-gray-50 dark:bg-[#0d1117] rounded-lg px-3 py-2.5 border border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-5">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-gray-400">Alpha vs {bm} {hasRealized ? '(realized)' : '(look-through)'}</div>
            <div className={`text-2xl font-serif tabular-nums ${clr(headline)}`}>{pct(headline)}</div>
          </div>
          {hasRealized && (
            <div className="flex items-center gap-4 text-gray-500">
              <Stat label={scopeLabel || 'Portfolio'} value={pct(port)} tone={clr(port)} />
              <span className="text-gray-300 dark:text-gray-600 text-lg">−</span>
              <Stat label={bm} value={pct(bench)} tone={clr(bench)} />
            </div>
          )}
        </div>
        <div className="text-[10px] text-gray-400 text-right max-w-[300px]">
          Decomposition sums to <span className={clr(data.active_return)}>{pct(data.active_return)}</span>
          {residual != null && <> · residual <span className="text-gray-500">{pct(residual)}</span></>}
        </div>
      </div>

      {/* Honest caveat — differs by method */}
      <div className="text-[10px] text-amber-600 dark:text-amber-500/90 bg-amber-500/8 border border-amber-500/25 rounded px-2 py-1.5 leading-relaxed">
        {isDaily ? (
          <>Daily-weighted{data.n_days ? ` (${data.n_days} days)` : ''}: each day's actual holdings × that day's return, geometric-linked — a position opened mid-window is credited only while held. Decomposes the <em>invested</em> return, so the residual vs total-equity TWR (mostly cash drag) is expected.</>
        ) : (
          <>Directional: point-in-time look-through weights (as-of {anchorNote}) × total return over the window — signs &amp; ranking robust, absolute bps approximate. Underweighting a name that <em>lagged</em> {bm} is positive; overweighting one that <em>beat</em> it is positive.</>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {isDaily
          ? <SectorTotals rows={data.by_sector || []} />
          : <SectorBrinson rows={data.by_sector || []} totals={data.totals} />}
        <SecurityActive rows={data.by_security || []} benchmark={bm} />
      </div>
    </div>
  );
};

// ── Panel shell + mode toggle ────────────────────────────────────────────────
const ModeBtn = ({ active, onClick, children }) => (
  <button onClick={onClick}
    className={`px-2.5 py-1 text-[10px] uppercase tracking-wider rounded transition-colors ${
      active ? 'bg-brand-gold/15 text-brand-gold' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>
    {children}
  </button>
);

const AttributionPanel = ({ scope, realizedPort, realizedBench, scopeLabel }) => {
  const [mode, setMode] = useState('active');   // default: answer "where's my alpha"
  const [abs, setAbs] = useState(null);
  const [act, setAct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const key = JSON.stringify(scope || {});

  useEffect(() => { setAbs(null); setAct(null); }, [key]);   // new scope → drop caches
  useEffect(() => {
    const have = mode === 'active' ? act : abs;
    if (have) return;
    setLoading(true); setError(null);
    const req = mode === 'active'
      ? tradingService.getPortfolioActiveAttribution({ ...(scope || {}) })
      : tradingService.getPortfolioAttribution({ ...(scope || {}) });
    req.then((r) => (mode === 'active' ? setAct(r.data) : setAbs(r.data)))
      .catch((e) => setError(e?.response?.data?.error || 'Failed to load attribution'))
      .finally(() => setLoading(false));
  }, [key, mode]);   // eslint-disable-line react-hooks/exhaustive-deps

  const data = mode === 'active' ? act : abs;

  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-3">
          <div className="text-[10px] uppercase tracking-wider text-gray-400">Attribution</div>
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-[#0d1117] rounded-md p-0.5">
            <ModeBtn active={mode === 'active'} onClick={() => setMode('active')}>Active vs SPY</ModeBtn>
            <ModeBtn active={mode === 'absolute'} onClick={() => setMode('absolute')}>Absolute</ModeBtn>
          </div>
        </div>
        {data?.window && (
          <div className="text-[10px] text-gray-400 flex items-center gap-2">
            <span>{data.window[0]} → {data.window[1]}</span>
            {mode === 'absolute' && data.reconciled != null && (
              <span className={data.reconciled ? 'text-emerald-500' : 'text-red-500'}
                title={data.reconciled ? 'Strategy contributions sum to the portfolio TWR' : 'Did not reconcile — see logs'}>
                {data.reconciled ? '✓ reconciles' : '⚠ residual'} · TWR {pct(data.portfolio_twr)}
              </span>
            )}
          </div>
        )}
      </div>

      {error && <div className="text-sm text-red-500">{error}</div>}
      {loading && !data && <div className="h-24 skeleton rounded-lg" />}

      {data && !loading && (mode === 'active'
        ? <ActiveView data={data} realizedPort={realizedPort} realizedBench={realizedBench} scopeLabel={scopeLabel} />
        : <AbsoluteView data={data} />)}
    </div>
  );
};

export default AttributionPanel;
